// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../store';
import { Sidebar } from '../../../../components/Sidebar';
import axiosClient from '../../../../api/AxiosClient';
import { fetchInterviewHostDashboard, CandidatesData, Candidate } from '../../../../store/interviewHostSlice';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './reports.module.scss';

type Agreement = 'Aligned' | 'Overridden' | 'Pending' | 'Not available';
type Verdict = 'positive' | 'negative' | 'neutral' | 'none';

interface ReportRow {
  candidate: Candidate;
  jobTitle: string;
  aiRecommendation: string;
  aiVerdict: Verdict;
  recruiterDecision: string;
  recruiterVerdict: Verdict;
  agreement: Agreement;
}

const classify = (value?: string | null): Verdict => {
  if (!value) return 'none';
  const v = value.toLowerCase();
  if (/(reject|declin|not recommend|no hire|no-hire|fail|do not)/.test(v)) return 'negative';
  if (/(approv|select|advance|hire|proceed|recommend|strong|pass|shortlist)/.test(v)) return 'positive';
  return 'neutral';
};

const verdictClass = (v: Verdict): string =>
  ({ positive: styles.success, negative: styles.error, neutral: styles.warning, none: styles.muted })[v];

const agreementClass = (a: Agreement): string =>
  ({ Aligned: styles.success, Overridden: styles.error, Pending: styles.muted, 'Not available': styles.muted })[a];

const scoreClass = (n: number): string => (n >= 70 ? styles.scoreHigh : n >= 40 ? styles.scoreMid : styles.scoreLow);

const ScoreChip: React.FC<{ value?: number | null; suffix?: string }> = ({ value, suffix = '' }) =>
  value === null || value === undefined ? (
    <span className={styles.dash}>—</span>
  ) : (
    <span className={`${styles.score} ${scoreClass(value)}`}>
      {value}
      {suffix}
    </span>
  );

const formatDate = (d?: string | null): string => (d ? new Date(d).toLocaleDateString() : '—');

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { data } = useSelector((state: RootState) => state.interviewHost);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [failedJobs, setFailedJobs] = useState(0);
  const [search, setSearch] = useState('');
  const [jobFilter, setJobFilter] = useState('All');
  const [agreementFilter, setAgreementFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Always refresh on open so jobs/candidates added recently are included
  useEffect(() => {
    dispatch(fetchInterviewHostDashboard());
  }, [dispatch]);

  // Build the report from the loaded jobs list + each job's candidates list API
  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    setLoading(true);
    Promise.allSettled(
      data.jobs.map((job) => axiosClient.get<CandidatesData>(`/interview-host/jobs/${job.job_id}/candidates`))
    ).then((results) => {
      if (cancelled) return;
      const built: ReportRow[] = [];
      let failed = 0;
      results.forEach((res, i) => {
        if (res.status !== 'fulfilled') {
          failed += 1;
          return;
        }
        res.value.data.candidates.forEach((c) => {
          const ai = c.report_summary?.recommendation || c.screening?.recommendation || '';
          const aiVerdict = classify(ai);
          const recruiterDecision = c.decision?.outcome || '';
          const recruiterVerdict = classify(recruiterDecision);
          let agreement: Agreement = 'Pending';
          if (aiVerdict === 'none') {
            agreement = 'Not available';
          } else if (recruiterDecision) {
            agreement = aiVerdict === recruiterVerdict ? 'Aligned' : 'Overridden';
          }
          built.push({
            candidate: c,
            jobTitle: data.jobs[i].title,
            aiRecommendation: ai,
            aiVerdict,
            recruiterDecision,
            recruiterVerdict,
            agreement
          });
        });
      });
      setRows(built);
      setFailedJobs(failed);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (jobFilter === 'All' || r.jobTitle === jobFilter) &&
        (agreementFilter === 'All' || r.agreement === agreementFilter) &&
        (!q || `${r.candidate.name} ${r.candidate.email} ${r.jobTitle}`.toLowerCase().includes(q))
    );
  }, [rows, search, jobFilter, agreementFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize]
  );
  const rangeStart = filtered.length ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, filtered.length);

  useEffect(() => {
    setPage(1);
  }, [search, jobFilter, agreementFilter, pageSize]);

  const exportCsv = (): void => {
    const head = [
      'Candidate',
      'Email',
      'Job',
      'JD Match %',
      'Interview Score',
      'AI Recommendation',
      'Recruiter Decision',
      'Decided By',
      'Decided On',
      'Agreement'
    ];
    const esc = (v: unknown): string => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = filtered.map((r) =>
      [
        r.candidate.name,
        r.candidate.email,
        r.jobTitle,
        r.candidate.jd_match,
        r.candidate.interview_score,
        r.aiRecommendation,
        r.recruiterDecision,
        r.candidate.decision?.decided_by,
        r.candidate.decision?.decided_at,
        r.agreement
      ]
        .map(esc)
        .join(',')
    );
    const blob = new Blob([[head.map(esc).join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'selection-decisions-report.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleLogout = (): void => {
    window.location.href = '/api/sso/logout';
  };

  const jobTitles = Array.from(new Set(rows.map((r) => r.jobTitle)));

  return (
    <div className={styles.container}>
      <Sidebar activeNav="reports" user={user || { name: data?.recruiter?.name }} onLogout={handleLogout} />
      <main className={styles.mainContent}>
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <button className={styles.breadcrumbLink} onClick={() => navigate('/')}>
            Home
          </button>
          <ChevronRightIcon className={styles.breadcrumbSep} fontSize="small" />
          <span className={styles.breadcrumbCurrent}>Reports</span>
        </nav>
        <h1 className={styles.title}>Selection Decisions Report</h1>
        <p className={styles.subtitle}>AI recommendation vs. recruiter decision across all roles and candidates.</p>

        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <SearchIcon className={styles.searchIcon} fontSize="small" />
            <input
              className={styles.input}
              placeholder="Search candidate, email or job"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className={styles.select} value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}>
            <option value="All">All jobs</option>
            {jobTitles.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select
            className={styles.select}
            value={agreementFilter}
            onChange={(e) => setAgreementFilter(e.target.value)}
          >
            <option value="All">All agreement</option>
            <option value="Aligned">Aligned</option>
            <option value="Overridden">Overridden</option>
            <option value="Pending">Pending</option>
            <option value="Not available">Not available</option>
          </select>
          <span className={styles.spacer} />
          <span className={styles.resultCount}>{loading ? 'Loading…' : `${filtered.length} candidates`}</span>
          <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={exportCsv} disabled={!filtered.length}>
            <FileDownloadOutlinedIcon fontSize="small" />
            Export CSV
          </button>
        </div>
        {failedJobs > 0 && <div className={styles.note}>{failedJobs} job(s) could not be loaded.</div>}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Job</th>
                <th>JD match</th>
                <th>Interview score</th>
                <th>AI recommendation</th>
                <th>Recruiter decision</th>
                <th>Decided by</th>
                <th>Decided on</th>
                <th>Agreement</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 9 }).map((__, j) => (
                      <td key={j}>
                        <div className={styles.skeleton} style={{ width: j === 0 ? 180 : 70 }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className={styles.empty}>
                    <div className={styles.emptyTitle}>No candidates found</div>
                    Try changing the search or filters.
                  </td>
                </tr>
              ) : (
                pagedRows.map((r) => (
                  <tr key={r.candidate.candidate_id}>
                    <td>
                      <div className={styles.candidateCell}>
                        <div className={styles.avatar}>{(r.candidate.name || '?').charAt(0).toUpperCase()}</div>
                        <div>
                          <button
                            className={styles.link}
                            onClick={() =>
                              navigate(`/jobs/${r.candidate.job_id}/candidates/${r.candidate.candidate_id}/report`)
                            }
                          >
                            {r.candidate.name}
                          </button>
                          <div className={styles.email}>{r.candidate.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className={styles.jobCell}>{r.jobTitle}</td>
                    <td>
                      <ScoreChip value={r.candidate.jd_match} suffix="%" />
                    </td>
                    <td>
                      <ScoreChip value={r.candidate.interview_score} />
                    </td>
                    <td>
                      <span className={`${styles.badge} ${verdictClass(r.aiVerdict)}`}>
                        {r.aiRecommendation || 'Not available'}
                      </span>
                    </td>
                    <td>
                      <span className={`${styles.badge} ${verdictClass(r.recruiterVerdict)}`}>
                        {r.recruiterDecision || 'Pending'}
                      </span>
                    </td>
                    <td>{r.candidate.decision?.decided_by || '—'}</td>
                    <td>{formatDate(r.candidate.decision?.decided_at)}</td>
                    <td>
                      <span className={`${styles.badge} ${agreementClass(r.agreement)}`}>{r.agreement}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.pagination}>
          <span>
            Showing {rangeStart}–{rangeEnd} of {filtered.length}
          </span>
          <label>
            Rows per page{' '}
            <select className={styles.select} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {[20, 25, 30].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <button className={styles.btn} onClick={() => setPage(currentPage - 1)} disabled={currentPage <= 1}>
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button className={styles.btn} onClick={() => setPage(currentPage + 1)} disabled={currentPage >= totalPages}>
            Next
          </button>
        </div>
      </main>
    </div>
  );
};

export default Reports;
